/**
 * Smoothing model behind the upload progress readout — "65.0 MB/s · 24s left" (ui#942).
 *
 * Byte progress is bursty by construction: several parallel parts report their sent bytes and settle
 * within the same millisecond (bytes arrive as the browser sends them since ui#2933, and again when a
 * part is acknowledged), and the pipeline width climbs underneath (`partConcurrency`/`adaptiveDepth`).
 * Rendering that stream directly makes the speed flash and the ETA oscillate, which is what the owner
 * reported. So the MODEL is smoothed here and the view only animates between the values this
 * publishes — no rule about what to show lives in the component.
 *
 * Three stages, in order:
 *
 * 1. **fold** — cumulative byte counts become interval rates over at least {@link MIN_FOLD_MS}, so a
 *    clump of parts settling 4 ms apart is measured over the interval it really took, not as a
 *    gigabyte-per-second spike. Nothing is lost by skipping a too-short interval: the counter is
 *    cumulative, so the bytes are folded by the next interval that is long enough.
 * 2. **smooth** — a bias-corrected exponential moving average with a time constant of
 *    {@link RATE_TIME_CONSTANT_MS}. Bias correction (dividing by the accumulated weight) is what
 *    lets the very first interval be reported at its own value instead of a fraction of it, so no
 *    warm-up ramp climbs out of zero and no seeding heuristic is needed.
 * 3. **gate** — at most one readout per {@link READOUT_CADENCE_MS} reaches the view, and its ETA is
 *    counted down like a clock between publishes, re-syncing to the fresh estimate only when the two
 *    genuinely disagree ({@link ETA_RELATIVE_TOLERANCE} / {@link ETA_ABSOLUTE_TOLERANCE_SECONDS}),
 *    and then only by {@link ETA_RESYNC_FRACTION} of the disagreement at a time.
 *    A rate that jitters ±40% around its mean therefore renders as a steady countdown rather than a
 *    number that flips every few hundred milliseconds.
 *
 * The caller drives the clock (every method takes `at`), which makes the whole model testable
 * against synthetic streams with no timers and no React.
 */

/** Publishing cadence: the view is handed a new readout at most this often. */
export const READOUT_CADENCE_MS = 1000;

/** EMA time constant — how long a burst keeps influencing the displayed rate. */
export const RATE_TIME_CONSTANT_MS = 4000;

/** Shortest interval that may be turned into a rate sample; shorter ones accumulate. */
export const MIN_FOLD_MS = 250;

/** Fraction of the running countdown a fresh estimate may differ by before the ETA re-syncs. */
export const ETA_RELATIVE_TOLERANCE = 0.15;

/** Floor under the relative tolerance, so a nearly-finished countdown is not re-synced every second. */
export const ETA_ABSOLUTE_TOLERANCE_SECONDS = 2;

/**
 * Share of a genuine disagreement that one publish closes. Below 1 on purpose: see {@link
 * ProgressReadout.etaFor} — landing on an estimate that is itself still moving is what makes a
 * countdown saw-tooth.
 */
export const ETA_RESYNC_FRACTION = 0.35;

/**
 * Below this rate the remaining-bytes quotient is hours of arithmetic noise (and at exactly zero it
 * is `Infinity`), so the last usable estimate is held instead.
 */
export const ETA_STALL_FLOOR_BYTES_PER_SECOND = 1024;

/** What the progress card should display right now. */
export interface ProgressReadoutValues {
	/** Smoothed transfer rate, in bytes per second. */
	readonly bytesPerSecond: number;
	/** Seconds until the transfer finishes at that rate, after the countdown/hysteresis pass. */
	readonly etaSeconds: number;
}

/** Overridable model constants; every field defaults to the exported constant of the same meaning. */
export interface ProgressReadoutSettings {
	readonly cadenceMs?: number;
	readonly rateTimeConstantMs?: number;
	readonly minFoldMs?: number;
	readonly etaRelativeTolerance?: number;
	readonly etaAbsoluteToleranceSeconds?: number;
	readonly etaResyncFraction?: number;
	readonly etaStallFloorBytesPerSecond?: number;
}

export class ProgressReadout {
	private readonly cadenceMs: number;
	private readonly timeConstantMs: number;
	private readonly minFoldMs: number;
	private readonly relativeTolerance: number;
	private readonly absoluteToleranceSeconds: number;
	private readonly resyncFraction: number;
	private readonly stallFloor: number;

	/** Time and cumulative byte count of the last interval folded into the average. */
	private foldedAt: number | null = null;
	private foldedBytes = 0;
	/** Numerator and denominator of the bias-corrected EMA; their ratio is the smoothed rate. */
	private weightedRate = 0;
	private weight = 0;
	/** The readout the view is currently showing, kept by reference so callers can detect a change. */
	private current: ProgressReadoutValues | null = null;
	private publishedAt = 0;
	private nextPublishAt = 0;

	constructor(settings: ProgressReadoutSettings = {}) {
		this.cadenceMs = settings.cadenceMs ?? READOUT_CADENCE_MS;
		this.timeConstantMs = settings.rateTimeConstantMs ?? RATE_TIME_CONSTANT_MS;
		this.minFoldMs = settings.minFoldMs ?? MIN_FOLD_MS;
		this.relativeTolerance = settings.etaRelativeTolerance ?? ETA_RELATIVE_TOLERANCE;
		this.absoluteToleranceSeconds = settings.etaAbsoluteToleranceSeconds ?? ETA_ABSOLUTE_TOLERANCE_SECONDS;
		this.resyncFraction = settings.etaResyncFraction ?? ETA_RESYNC_FRACTION;
		this.stallFloor = settings.etaStallFloorBytesPerSecond ?? ETA_STALL_FLOOR_BYTES_PER_SECOND;
	}

	/**
	 * Feeds one observation of the cumulative byte counter and returns what to display.
	 *
	 * Call it on every progress event AND on a timer while the upload runs: an observation that
	 * carries no new bytes is what tells the model the transfer has gone quiet, and it is the only
	 * thing that can move the readout while nothing is arriving. The returned object is the SAME
	 * reference between publishes, so an adapter can re-render on `!==` alone; `null` until the first
	 * cadence window closes.
	 */
	observe(bytesSent: number, totalBytes: number, at: number): ProgressReadoutValues | null {
		if (this.foldedAt === null) {
			// The first observation is a baseline, not a sample: bytes already sent before the model
			// was attached did not happen in zero time, and dividing by that would invent a rate.
			this.foldedAt = at;
			this.foldedBytes = bytesSent;
			this.nextPublishAt = at + this.cadenceMs;
			return null;
		}
		this.fold(bytesSent, at);
		this.publish(Math.max(0, totalBytes - bytesSent), at);
		return this.current;
	}

	/** The readout as last published, without advancing the model. */
	get displayed(): ProgressReadoutValues | null {
		return this.current;
	}

	private fold(bytesSent: number, at: number): void {
		const elapsedMs = at - this.foldedAt!;
		if (elapsedMs < this.minFoldMs) {
			return;
		}
		const instantRate = Math.max(0, bytesSent - this.foldedBytes) / elapsedMs * 1000;
		const decay = Math.exp(-elapsedMs / this.timeConstantMs);
		this.weightedRate = this.weightedRate * decay + instantRate * (1 - decay);
		this.weight = this.weight * decay + (1 - decay);
		this.foldedAt = at;
		this.foldedBytes = bytesSent;
	}

	private publish(remainingBytes: number, at: number): void {
		// The weight guard is not decoration: `cadenceMs` and `minFoldMs` are both settable, and a
		// caller that gates faster than it folds reaches the first window with no rate at all.
		if (at < this.nextPublishAt || this.weight === 0) {
			return;
		}
		const bytesPerSecond = this.weightedRate / this.weight;
		const etaSeconds = this.etaFor(remainingBytes, bytesPerSecond, at);
		if (etaSeconds === null) {
			return;
		}
		this.current = {bytesPerSecond, etaSeconds};
		this.publishedAt = at;
		this.nextPublishAt = at + this.cadenceMs;
	}

	/**
	 * The ETA to display: the running countdown, re-synced to the fresh estimate only when the two
	 * have genuinely diverged. Null only before the first publish while the rate is unusable —
	 * showing "3h" for one frame because the first parts have not landed yet is worse than waiting.
	 */
	private etaFor(remainingBytes: number, bytesPerSecond: number, at: number): number | null {
		const estimate = bytesPerSecond >= this.stallFloor ? remainingBytes / bytesPerSecond : null;
		const previous = this.current;
		if (previous === null) {
			return estimate;
		}
		if (estimate === null) {
			return previous.etaSeconds;
		}
		const countedDown = Math.max(0, previous.etaSeconds - (at - this.publishedAt) / 1000);
		const tolerance = Math.max(this.absoluteToleranceSeconds, this.relativeTolerance * countedDown);
		if (Math.abs(estimate - countedDown) <= tolerance) {
			return countedDown;
		}
		// Outside the band the countdown is wrong — but LANDING on the estimate is what made the
		// readout saw-tooth. After a rate change the EMA is still catching up, so the estimate is
		// itself still moving: the countdown jumps onto it, ticks away from it again, and jumps
		// again, which is the "jumps too often" complaint in a new costume. Closing a FRACTION of
		// the disagreement per publish converges on the truth within a few seconds and renders as
		// the countdown bending rather than snapping.
		return countedDown + (estimate - countedDown) * this.resyncFraction;
	}
}
